import { FocusHeading } from "@kovernow/react-ui/a11y/focus-heading";
import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useT } from "../core/use-t";

/**
 * Every page: an `h1` that takes focus on route change (not on first load),
 * and `document.title` "{Page} — {Flow} — {Tenant}" (CAP-10).
 */
export function Page({
  titleKey,
  titleValues,
  flowKey,
  headingKey,
  headingValues,
  focusKey,
  children,
  className,
}: {
  titleKey: string;
  /** Interpolation values for the document title (e.g. "Step 2 of 5"). */
  titleValues?: Record<string, unknown>;
  flowKey?: string;
  /** Visible heading when it differs from the document title. */
  headingKey?: string;
  /** Interpolation values for the visible heading (e.g. the greeting name). */
  headingValues?: Record<string, unknown>;
  /**
   * Overrides route-change focus, e.g. a flow step id. `null` = never
   * auto-focus (e.g. a page arrived at on first load whose URL then changes).
   */
  focusKey?: string | null;
  children?: ReactNode;
  className?: string;
}) {
  const t = useT();
  const location = useLocation();
  const page = t(titleKey, titleValues);
  useEffect(() => {
    const flow = flowKey ? t(flowKey) : undefined;
    // A flow's first page often shares its name ("Sign in — Sign in"): say it once.
    document.title =
      flow && flow !== page
        ? t("common.pageTitleInFlow", { page, flow })
        : t("common.pageTitle", { page });
  }, [page, flowKey, t]);
  // React Router's initial location key is "default": don't steal focus on load.
  const routeKey = location.key === "default" ? undefined : location.key;
  return (
    <div className={className ?? "mx-auto w-full max-w-3xl px-4 py-6 xl:px-8"}>
      <FocusHeading focusKey={focusKey === null ? undefined : focusKey ?? routeKey} className="mb-4">
        {headingKey ? t(headingKey, headingValues) : page}
      </FocusHeading>
      {children}
    </div>
  );
}
